import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Zap, Flame, Shield, Clock, TrendingUp, Users, BarChart3,
  AlertTriangle, RefreshCw, Loader2, ChevronRight, Award,
  Activity, Target, Gauge, ArrowUpRight, ArrowDownRight,
  Coins, Lock, Settings2
} from 'lucide-react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Slider } from '@/components/ui/slider';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Stat Card ──────────────────────────────────────────
function StatCard({ label, value, sub, icon: Icon, color = "text-cyan-400" }) {
  return (
    <Card className="bg-[#0a0a0f] border-white/5">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">{label}</p>
            <p className="text-xl font-bold text-white mt-1" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>{value}</p>
            {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
          </div>
          {Icon && <Icon className={`w-5 h-5 ${color} opacity-60`} />}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Score Ring ──────────────────────────────────────────
function ScoreRing({ vs, maxVs = 2000 }) {
  const pct = Math.min((vs / maxVs) * 100, 100);
  const r = 70;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;

  return (
    <div className="relative w-48 h-48 mx-auto">
      <svg viewBox="0 0 160 160" className="w-full h-full -rotate-90">
        <circle cx="80" cy="80" r={r} fill="none" stroke="#1a1a2e" strokeWidth="8" />
        <circle
          cx="80" cy="80" r={r} fill="none"
          stroke="url(#vsGradient)" strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circ} strokeDashoffset={offset}
          className="transition-all duration-1000"
        />
        <defs>
          <linearGradient id="vsGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#06b6d4" />
            <stop offset="100%" stopColor="#8b5cf6" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold text-white">{vs.toFixed(1)}</span>
        <span className="text-xs text-gray-500 uppercase tracking-wider">Velocity</span>
      </div>
    </div>
  );
}

// ── Epoch Countdown ────────────────────────────────────
function EpochCountdown({ remainingSeconds }) {
  const days = Math.floor(remainingSeconds / 86400);
  const hrs = Math.floor((remainingSeconds % 86400) / 3600);
  const mins = Math.floor((remainingSeconds % 3600) / 60);
  return (
    <div className="flex gap-3 justify-center" data-testid="epoch-countdown">
      {[{ v: days, l: 'D' }, { v: hrs, l: 'H' }, { v: mins, l: 'M' }].map(({ v, l }) => (
        <div key={l} className="bg-[#0a0a1a] rounded-lg px-3 py-2 min-w-[48px] text-center border border-white/5">
          <div className="text-lg font-mono font-bold text-white">{String(v).padStart(2, '0')}</div>
          <div className="text-[10px] text-gray-600 uppercase">{l}</div>
        </div>
      ))}
    </div>
  );
}

// ── Main Dashboard ─────────────────────────────────────
export default function VelocityDashboard() {
  const { user, token } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [protocolStats, setProtocolStats] = useState(null);
  const [epochInfo, setEpochInfo] = useState(null);
  const [myDashboard, setMyDashboard] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [burnMultiplier, setBurnMultiplier] = useState([1.5]);
  const [burnCost, setBurnCost] = useState(null);
  const [burnLoading, setBurnLoading] = useState(false);
  const [tab, setTab] = useState("overview");

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, epochRes, lbRes] = await Promise.all([
        axios.get(`${API}/api/synergy/protocol-stats`),
        axios.get(`${API}/api/synergy/epoch/current`),
        axios.get(`${API}/api/synergy/leaderboard?limit=10`),
      ]);
      setProtocolStats(statsRes.data);
      setEpochInfo(epochRes.data);
      setLeaderboard(lbRes.data);

      if (token) {
        const dashRes = await axios.get(`${API}/api/synergy/dashboard/me`, { headers });
        setMyDashboard(dashRes.data);
      }
    } catch (e) {
      console.error("Failed to load synergy data", e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Burn cost preview
  useEffect(() => {
    const fetchBurnCost = async () => {
      try {
        const res = await axios.get(`${API}/api/synergy/burn/cost?multiplier=${burnMultiplier[0]}`);
        setBurnCost(res.data);
      } catch (e) { /* ignore */ }
    };
    fetchBurnCost();
  }, [burnMultiplier]);

  const handleBurn = async () => {
    if (!token) return;
    setBurnLoading(true);
    try {
      const res = await axios.post(`${API}/api/synergy/burn/execute`,
        { multiplier: burnMultiplier[0] }, { headers }
      );
      toast({ title: "Burn Successful", description: res.data.message });
      fetchData();
    } catch (e) {
      toast({ title: "Burn Failed", description: e.response?.data?.detail || "Error", variant: "destructive" });
    } finally {
      setBurnLoading(false);
    }
  };

  const handlePayoutPref = async (currency) => {
    if (!token) return;
    try {
      const res = await axios.post(`${API}/api/synergy/payout-preference`,
        { currency }, { headers }
      );
      toast({ title: "Preference Updated", description: res.data.message });
      fetchData();
    } catch (e) {
      toast({ title: "Error", description: e.response?.data?.detail || "Failed", variant: "destructive" });
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Velocity Dashboard">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
        </div>
      </DashboardLayout>
    );
  }

  const ps = protocolStats || {};
  const ei = epochInfo || {};
  const md = myDashboard || {};
  const lb = leaderboard || {};
  const score = md.score || {};
  const crisisMode = ps.crisis_mode;

  return (
    <DashboardLayout title="Velocity Dashboard">
      <div className="space-y-6" data-testid="velocity-dashboard">
        {/* Crisis Mode Banner */}
        {crisisMode && (
          <motion.div
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-center gap-3"
            data-testid="crisis-mode-banner"
          >
            <AlertTriangle className="w-6 h-6 text-amber-400 shrink-0" />
            <div>
              <p className="text-amber-300 font-semibold">Crisis Mode Active</p>
              <p className="text-amber-400/70 text-sm">Vulture Multiplier: 3x Activity bonus. Add liquidity to capture elevated rewards.</p>
            </div>
          </motion.div>
        )}

        {/* Protocol Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <StatCard label="Epoch" value={`#${ps.current_epoch || 1}`} sub={`${ei.remaining_days || 0}d remaining`} icon={Clock} color="text-blue-400" />
          <StatCard label="Active Users" value={ps.active_users_this_epoch || 0} sub="this epoch" icon={Users} color="text-green-400" />
          <StatCard label="Tokens Burned" value={(ps.tokens_burned || 0).toLocaleString()} sub={`${ps.supply_burned_pct || 0}% of supply`} icon={Flame} color="text-orange-400" />
          <StatCard label="Total Distributed" value={`$${(ps.total_distributed || 0).toLocaleString()}`} sub={`${ps.completed_epochs || 0} epochs`} icon={Coins} color="text-yellow-400" />
          <StatCard label="Reserve" value={`$${(ps.reserve_balance_usd || 0).toLocaleString()}`} sub={crisisMode ? "CRISIS MODE" : "Peace Time"} icon={Shield} color={crisisMode ? "text-amber-400" : "text-emerald-400"} />
          <StatCard label="Architects" value={ps.active_architects || 0} sub="active modules" icon={Award} color="text-purple-400" />
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-[#0a0a0f] border border-white/5">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="burn" data-testid="tab-burn">Burn Lab</TabsTrigger>
            <TabsTrigger value="leaderboard" data-testid="tab-leaderboard">Leaderboard</TabsTrigger>
            <TabsTrigger value="reserve" data-testid="tab-reserve">Reserve</TabsTrigger>
          </TabsList>

          {/* ── OVERVIEW TAB ── */}
          <TabsContent value="overview">
            <div className="grid lg:grid-cols-3 gap-6 mt-4">
              {/* Left: Score Ring + Breakdown */}
              <Card className="bg-[#0a0a0f] border-white/5 lg:col-span-1">
                <CardContent className="p-6">
                  <h3 className="text-sm text-gray-500 uppercase tracking-wider mb-4 text-center">Your Velocity Score</h3>
                  <ScoreRing vs={score.vs || 0} />
                  <div className="mt-6 space-y-3">
                    {[
                      { label: "Capital (Ck)", value: score.ck || 0, max: 500, color: "bg-cyan-500", weight: `${(ps.weights?.capital || 0.4) * 100}%` },
                      { label: "Loyalty (Tk\u00B2)", value: score.tk_squared || 0, max: 2704, color: "bg-purple-500", weight: `${(ps.weights?.time || 0.35) * 100}%` },
                      { label: "Activity (Ak)", value: score.ak_boosted || 0, max: 2975, color: "bg-emerald-500", weight: `${(ps.weights?.activity || 0.25) * 100}%` },
                    ].map(({ label, value, max, color, weight }) => (
                      <div key={label}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-gray-400">{label} <span className="text-gray-600">({weight})</span></span>
                          <span className="text-white font-mono">{typeof value === 'number' ? value.toLocaleString() : value}</span>
                        </div>
                        <Progress value={Math.min((value / max) * 100, 100)} className={`h-1.5 ${color}`} />
                      </div>
                    ))}
                    {score.burn_multiplier > 1 && (
                      <div className="flex items-center gap-2 text-xs text-orange-400 mt-2">
                        <Flame className="w-3 h-3" />
                        <span>Burn Boost: {score.burn_multiplier}x active</span>
                      </div>
                    )}
                    {score.native_bonus && (
                      <div className="flex items-center gap-2 text-xs text-cyan-400 mt-1">
                        <Zap className="w-3 h-3" />
                        <span>KOJIRU payout bonus: 1.2x Vs</span>
                      </div>
                    )}
                  </div>

                  {/* Payout Preference */}
                  <div className="mt-6 pt-4 border-t border-white/5">
                    <p className="text-xs text-gray-500 mb-2">Payout Preference</p>
                    <div className="flex gap-2">
                      {["USDC", "KOJIRU"].map((c) => (
                        <Button
                          key={c}
                          variant={md.payout_preference === c ? "default" : "outline"}
                          size="sm"
                          onClick={() => handlePayoutPref(c)}
                          data-testid={`payout-${c.toLowerCase()}`}
                          className="flex-1 text-xs"
                        >
                          {c} {c === "KOJIRU" && <Zap className="w-3 h-3 ml-1" />}
                        </Button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Center: Epoch Info + Activities */}
              <Card className="bg-[#0a0a0f] border-white/5">
                <CardContent className="p-6">
                  <h3 className="text-sm text-gray-500 uppercase tracking-wider mb-4">Current Epoch</h3>
                  <EpochCountdown remainingSeconds={ei.remaining_seconds || 0} />
                  <div className="grid grid-cols-2 gap-3 mt-4">
                    <div className="bg-[#111119] rounded-lg p-3 text-center">
                      <p className="text-lg font-bold text-white">{ei.activities_logged || 0}</p>
                      <p className="text-[10px] text-gray-500 uppercase">Activities</p>
                    </div>
                    <div className="bg-[#111119] rounded-lg p-3 text-center">
                      <p className="text-lg font-bold text-white">{md.activities_this_epoch || 0}</p>
                      <p className="text-[10px] text-gray-500 uppercase">Your Actions</p>
                    </div>
                  </div>

                  {/* Recent Activity */}
                  <h4 className="text-xs text-gray-500 uppercase tracking-wider mt-6 mb-2">Your Recent Actions</h4>
                  {(md.recent_activities || []).length > 0 ? (
                    <div className="space-y-1.5 max-h-40 overflow-y-auto">
                      {(md.recent_activities || []).slice(0, 8).map((a, i) => (
                        <div key={i} className="flex justify-between items-center text-xs bg-[#111119] rounded px-3 py-1.5">
                          <span className="text-gray-400 capitalize">{a.action_type?.replace(/_/g, ' ')}</span>
                          <span className="text-cyan-400 font-mono">+{a.points} pts</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-600 italic">No actions yet this epoch. Vote, evaluate, or repay to earn Activity points.</p>
                  )}

                  {/* Earnings History */}
                  <h4 className="text-xs text-gray-500 uppercase tracking-wider mt-6 mb-2">Payout History</h4>
                  {(md.payout_history || []).length > 0 ? (
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {md.payout_history.map((p, i) => (
                        <div key={i} className="flex justify-between text-xs bg-[#111119] rounded px-3 py-1.5">
                          <span className="text-gray-400">Epoch {p.epoch_number}</span>
                          <span className="text-green-400 font-mono">${p.payout_amount?.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-600 italic">No payouts yet. Distributions occur at the end of each epoch.</p>
                  )}
                  <div className="mt-3 text-right">
                    <span className="text-sm text-gray-400">Total Earned: </span>
                    <span className="text-sm font-bold text-green-400" data-testid="total-earned">${(md.total_earned || 0).toFixed(2)}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Right: Quick Leaderboard */}
              <Card className="bg-[#0a0a0f] border-white/5">
                <CardContent className="p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-sm text-gray-500 uppercase tracking-wider">Top Velocity</h3>
                    <Badge variant="outline" className="text-xs">{lb.participant_count || 0} users</Badge>
                  </div>
                  <div className="space-y-2">
                    {(lb.leaderboard || []).slice(0, 8).map((entry, i) => (
                      <div key={i} className="flex items-center gap-3 bg-[#111119] rounded-lg px-3 py-2">
                        <span className={`text-xs font-bold w-6 text-center ${i < 3 ? 'text-amber-400' : 'text-gray-600'}`}>
                          {entry.rank}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white truncate">{entry.name}</p>
                          <p className="text-[10px] text-gray-500">
                            Ck:{entry.ck.toFixed(0)} Tk\u00B2:{entry.tk_squared.toLocaleString()} Ak:{entry.ak_boosted.toFixed(0)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-cyan-400">{entry.vs.toFixed(1)}</p>
                          <p className="text-[10px] text-gray-500">{entry.share_pct}%</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button variant="ghost" size="sm" className="w-full mt-3 text-xs text-gray-500" onClick={() => setTab("leaderboard")}>
                    View Full Leaderboard <ChevronRight className="w-3 h-3 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ── BURN LAB TAB ── */}
          <TabsContent value="burn">
            <div className="grid lg:grid-cols-2 gap-6 mt-4">
              <Card className="bg-[#0a0a0f] border-white/5">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-6">
                    <Flame className="w-5 h-5 text-orange-400" />
                    <h3 className="text-lg font-semibold text-white">Burn-to-Boost Laboratory</h3>
                  </div>

                  <p className="text-sm text-gray-400 mb-6">
                    Permanently burn KOJIRU tokens to increase your Activity Score multiplier. Burned tokens are removed from circulating supply forever.
                  </p>

                  {/* Multiplier Slider */}
                  <div className="mb-6">
                    <div className="flex justify-between mb-2">
                      <label className="text-sm text-gray-400">Target Multiplier</label>
                      <span className="text-lg font-bold text-orange-400">{burnMultiplier[0].toFixed(1)}x</span>
                    </div>
                    <Slider
                      value={burnMultiplier}
                      onValueChange={setBurnMultiplier}
                      min={1.1} max={5.0} step={0.1}
                      className="mb-2"
                      data-testid="burn-slider"
                    />
                    <div className="flex justify-between text-[10px] text-gray-600">
                      <span>1.1x Entry</span>
                      <span>2.0x Power</span>
                      <span>5.0x Titan</span>
                    </div>
                  </div>

                  {/* Cost Breakdown */}
                  {burnCost && (
                    <div className="bg-[#111119] rounded-xl p-4 space-y-3 mb-6" data-testid="burn-cost-preview">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-400">Burn Cost</span>
                        <span className="text-sm font-bold text-white">{burnCost.burn_cost_kutro.toLocaleString()} KOJIRU</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-400">Duration</span>
                        <span className="text-sm text-white">{burnCost.duration_days} days ({burnCost.duration_epochs} epochs)</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-400">Annual Cost (13 renewals)</span>
                        <span className="text-sm text-white">{burnCost.annual_cost.toLocaleString()} KOJIRU</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-400">Scarcity Ratio</span>
                        <span className="text-sm text-white">{burnCost.scarcity_ratio}x</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-white/5">
                        <span className="text-sm text-gray-400">Revenue Increase</span>
                        <span className="text-sm font-bold text-green-400">+{((burnMultiplier[0] - 1) * 100).toFixed(0)}% weekly share</span>
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={handleBurn}
                    disabled={burnLoading || !token}
                    className="w-full bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500"
                    data-testid="burn-execute-btn"
                  >
                    {burnLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Flame className="w-4 h-4 mr-2" />}
                    Burn {burnCost?.burn_cost_kutro?.toLocaleString() || '...'} KOJIRU
                  </Button>
                </CardContent>
              </Card>

              {/* Global Burn Stats */}
              <Card className="bg-[#0a0a0f] border-white/5">
                <CardContent className="p-6">
                  <h3 className="text-sm text-gray-500 uppercase tracking-wider mb-4">Supply Dynamics</h3>

                  <div className="space-y-4">
                    <div className="bg-[#111119] rounded-xl p-4">
                      <p className="text-xs text-gray-500 mb-1">Circulating Supply</p>
                      <p className="text-2xl font-bold text-white">{(ps.circulating_supply || 100000000).toLocaleString()}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Progress value={((TOTAL_SUPPLY - (ps.circulating_supply || TOTAL_SUPPLY)) / TOTAL_SUPPLY) * 100} className="h-1.5 flex-1" />
                        <span className="text-xs text-orange-400">{ps.supply_burned_pct || 0}% burned</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-[#111119] rounded-lg p-3">
                        <p className="text-[10px] text-gray-500 uppercase">Total Burned</p>
                        <p className="text-lg font-bold text-orange-400">{(ps.tokens_burned || 0).toLocaleString()}</p>
                      </div>
                      <div className="bg-[#111119] rounded-lg p-3">
                        <p className="text-[10px] text-gray-500 uppercase">Total Boosts</p>
                        <p className="text-lg font-bold text-purple-400">{ps.total_boosts || 0}</p>
                      </div>
                    </div>

                    {/* Active Boosts for user */}
                    {(md.active_boosts || []).length > 0 && (
                      <div>
                        <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-2">Your Active Boosts</h4>
                        {md.active_boosts.map((b, i) => (
                          <div key={i} className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 flex justify-between items-center">
                            <div>
                              <span className="text-sm font-bold text-orange-400">{b.multiplier}x Boost</span>
                              <p className="text-xs text-gray-500">{b.tokens_burned} KOJIRU burned</p>
                            </div>
                            <Badge variant="outline" className="text-orange-400 border-orange-500/30">
                              Expires Epoch {b.end_epoch}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Cost Comparison Table */}
                    <div>
                      <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-2">Boost Cost Table</h4>
                      <div className="space-y-1">
                        {[
                          { m: "1.2x", label: "Entry", color: "text-gray-300" },
                          { m: "2.0x", label: "Power", color: "text-cyan-400" },
                          { m: "3.0x", label: "Elite", color: "text-purple-400" },
                          { m: "5.0x", label: "Titan", color: "text-orange-400" },
                        ].map(({ m, label, color }) => (
                          <div key={m} className="flex justify-between text-xs bg-[#111119] rounded px-3 py-2">
                            <span className={color}>{m} ({label})</span>
                            <span className="text-gray-400">{m === "1.2x" ? "~132" : m === "2.0x" ? "~283" : m === "3.0x" ? "~520" : "~1,118"} KOJIRU</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ── LEADERBOARD TAB ── */}
          <TabsContent value="leaderboard">
            <Card className="bg-[#0a0a0f] border-white/5 mt-4">
              <CardContent className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-white">Velocity Leaderboard</h3>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline">{lb.participant_count || 0} participants</Badge>
                    <Badge variant="outline" className="text-cyan-400 border-cyan-500/30">
                      Global Vs: {lb.total_vs?.toFixed(1) || 0}
                    </Badge>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="leaderboard-table">
                    <thead>
                      <tr className="border-b border-white/5 text-xs text-gray-500 uppercase">
                        <th className="py-2 text-left w-12">Rank</th>
                        <th className="py-2 text-left">User</th>
                        <th className="py-2 text-right">Capital (Ck)</th>
                        <th className="py-2 text-right">Loyalty (Tk&sup2;)</th>
                        <th className="py-2 text-right">Activity (Ak)</th>
                        <th className="py-2 text-right">Boost</th>
                        <th className="py-2 text-right">Vs</th>
                        <th className="py-2 text-right">Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(lb.leaderboard || []).map((entry) => (
                        <tr key={entry.user_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                          <td className={`py-3 font-bold ${entry.rank <= 3 ? 'text-amber-400' : 'text-gray-500'}`}>
                            {entry.rank}
                          </td>
                          <td className="py-3 text-white">{entry.name}</td>
                          <td className="py-3 text-right text-cyan-400 font-mono">{entry.ck.toFixed(1)}</td>
                          <td className="py-3 text-right text-purple-400 font-mono">{entry.tk_squared.toLocaleString()}</td>
                          <td className="py-3 text-right text-emerald-400 font-mono">{entry.ak_boosted.toFixed(0)}</td>
                          <td className="py-3 text-right">
                            {entry.burn_multiplier > 1 ? (
                              <Badge className="bg-orange-500/10 text-orange-400 border-orange-500/20">{entry.burn_multiplier}x</Badge>
                            ) : (
                              <span className="text-gray-600">1.0x</span>
                            )}
                          </td>
                          <td className="py-3 text-right font-bold text-white">{entry.vs.toFixed(1)}</td>
                          <td className="py-3 text-right text-gray-400">{entry.share_pct}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── RESERVE TAB ── */}
          <TabsContent value="reserve">
            <div className="grid lg:grid-cols-2 gap-6 mt-4">
              <Card className="bg-[#0a0a0f] border-white/5">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-6">
                    <Shield className={`w-5 h-5 ${crisisMode ? 'text-amber-400' : 'text-emerald-400'}`} />
                    <h3 className="text-lg font-semibold text-white">Anti-Fragile Reserve</h3>
                    <Badge className={crisisMode
                      ? "bg-amber-500/10 text-amber-400 border-amber-500/20 ml-auto"
                      : "bg-emerald-500/10 text-emerald-400 border-emerald-500/20 ml-auto"
                    } data-testid="reserve-mode-badge">
                      {crisisMode ? "CRISIS MODE" : "Peace Time"}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-6">
                    <div className="bg-[#111119] rounded-lg p-4">
                      <p className="text-[10px] text-gray-500 uppercase">USD Balance</p>
                      <p className="text-xl font-bold text-white">${(ps.reserve_balance_usd || 0).toLocaleString()}</p>
                    </div>
                    <div className="bg-[#111119] rounded-lg p-4">
                      <p className="text-[10px] text-gray-500 uppercase">KOJIRU Balance</p>
                      <p className="text-xl font-bold text-white">{(ps.reserve_balance_kutro || 0).toLocaleString()}</p>
                    </div>
                  </div>

                  <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-3">Circuit Breaker Triggers</h4>
                  <div className="space-y-2">
                    {[
                      { label: "BTC/ETH 24h drop", threshold: ">15%", icon: TrendingUp },
                      { label: "Pool utilization spike", threshold: ">3x 30d avg", icon: Activity },
                      { label: "Agent default rate", threshold: ">90th percentile", icon: AlertTriangle },
                    ].map(({ label, threshold, icon: Icon }) => (
                      <div key={label} className="flex items-center gap-3 bg-[#111119] rounded-lg px-3 py-2">
                        <Icon className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-400 flex-1">{label}</span>
                        <Badge variant="outline" className="text-xs">{threshold}</Badge>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-600 mt-3 italic">Crisis Mode activates when 2 of 3 triggers are met.</p>
                </CardContent>
              </Card>

              <Card className="bg-[#0a0a0f] border-white/5">
                <CardContent className="p-6">
                  <h3 className="text-sm text-gray-500 uppercase tracking-wider mb-4">Response Matrix</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-white/5 text-gray-500 uppercase">
                          <th className="py-2 text-left">Feature</th>
                          <th className="py-2 text-center">Peace Time</th>
                          <th className="py-2 text-center">Crisis Mode</th>
                        </tr>
                      </thead>
                      <tbody className="text-gray-300">
                        {[
                          ["Revenue to Treasury", "10%", "0%"],
                          ["Velocity Multiplier", "1.0x", "2.0x - 3.0x"],
                          ["Liquidation Grace", "None", "60 min (top 20% Vs)"],
                          ["Stability Bonus", "1.0x", "3.0x Vulture"],
                          ["Reserve Deployment", "0%", "100% floods Pool"],
                        ].map(([feature, peace, crisis]) => (
                          <tr key={feature} className="border-b border-white/5">
                            <td className="py-2 text-gray-400">{feature}</td>
                            <td className="py-2 text-center">{peace}</td>
                            <td className="py-2 text-center text-amber-400 font-medium">{crisis}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <h4 className="text-xs text-gray-500 uppercase tracking-wider mt-6 mb-3">ESF Quick Calculator</h4>
                  <div className="space-y-1.5">
                    {[
                      { days: "<7 days", base: "5.0%", crisis: "15.0%", dest: "100% burned" },
                      { days: "8-30 days", base: "2.5%", crisis: "7.5%", dest: "50/50 burn/reserve" },
                      { days: ">30 days", base: "0.5%", crisis: "2.5%", dest: "100% to reserve" },
                    ].map(({ days, base, crisis, dest }) => (
                      <div key={days} className="flex items-center gap-2 bg-[#111119] rounded px-3 py-2 text-xs">
                        <span className="text-gray-400 w-20">{days}</span>
                        <span className="text-white flex-1">{base}</span>
                        <span className="text-amber-400 flex-1">Crisis: {crisis}</span>
                        <span className="text-gray-500 text-[10px]">{dest}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}

const TOTAL_SUPPLY = 100_000_000;
